// What search engines read on the screener: a token's head and the plain text under the app, built
// by server.ts from a token row. A token's name and symbol are whatever its creator wrote on chain,
// so everything here is escaped, and a name that advertises something keeps the page out of search.

import { compact, pct, price } from "./format";

export const SITE = "https://screener.aurelia.build";
/** Below these a token's page is thin, and most tokens are below them: it stays out of search. */
export const MIN_LIQUIDITY_USD = 10_000;
export const MIN_VOLUME_24H_USD = 1_000;
/** IndexNow's proof that we own this host: public by design, served at /<key>.txt on each site. */
export const INDEXNOW_KEY = "95cf7caad7f5981624117bc1356550a4";

export type Row = {
  chain: string;
  token: { address: string; symbol: string | null; name: string | null };
  priceUsd: number | null;
  change?: Partial<Record<"5m" | "1h" | "6h" | "24h", number | null>> | null;
  marketCapUsd: number | null;
  fdvUsd?: number | null;
  liquidityUsd: number | null;
  volume24hUsd: number | null;
  trades24h: number | null;
  buys24h?: number | null;
  sells24h?: number | null;
  traders24h: number | null;
  createdAt?: number | null;
  launchpad: { id: string; complete: boolean } | null;
  flags: string[] | null;
};

export const esc = (s: string) =>
  s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
/** Inside a <script type="application/ld+json">: "<" could end the script. */
const jsonLd = (v: unknown) => JSON.stringify(v).replaceAll("<", "\\u003c");

/** On-chain text as one short line: no control characters, no runs of spaces. */
const clean = (s: string | null, max: number) => (s ?? "").replace(/[\p{C}\s]+/gu, " ").trim().slice(0, max);
const ADVERT = /[<>]|https?:|www\.|\.(com|io|xyz|net|org|app|fun|gg|me|co|ai)\b|t\.me|airdrop|claim|giveaway|free\b/i;
// A word like these can get a whole page treated as adult content. The app still lists such a token.
const EXPLICIT = /fuck|shit|porn|titty|titties|boob|nigg|fagg|hitler|nazi|\b(sex|cum|dick|cock|pussy|rape|tits)\b/i;

/** Whether a token's page should be in search: enough market to be worth a page, and nothing that says it is a trap. */
export function indexable(r: Row): boolean {
  const symbol = clean(r.token.symbol, 24);
  const name = clean(r.token.name, 60);
  return !!symbol && !!name
    && !ADVERT.test(`${symbol} ${name}`)
    && !EXPLICIT.test(`${symbol} ${name}`)
    && (r.liquidityUsd ?? 0) >= MIN_LIQUIDITY_USD
    && (r.volume24hUsd ?? 0) >= MIN_VOLUME_24H_USD
    && r.priceUsd != null
    && !r.flags?.includes("honeypot");
}

export type Head = { title: string; description: string; canonical?: string; robots?: string; ld?: unknown };

/** The shell's head with this page's in place of its generic title and description. */
export function withHead(shell: string, head: Head): string {
  const tags = [
    `<title>${esc(head.title)}</title>`,
    `<meta name="description" content="${esc(head.description)}" />`,
    head.robots ? `<meta name="robots" content="${esc(head.robots)}" />` : "",
    head.canonical ? `<link rel="canonical" href="${esc(head.canonical)}" />` : "",
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="Aurelia Screener" />`,
    `<meta property="og:title" content="${esc(head.title)}" />`,
    `<meta property="og:description" content="${esc(head.description)}" />`,
    head.canonical ? `<meta property="og:url" content="${esc(head.canonical)}" />` : "",
    head.ld ? `<script type="application/ld+json">${jsonLd(head.ld)}</script>` : "",
  ].filter(Boolean).join("\n    ");
  // The generic tags go first, or these patterns would match the new ones.
  return shell
    .replace(/<meta name="description"[^>]*>\s*/, () => "")
    .replace(/<meta property="og:title"[^>]*>\s*/, () => "")
    .replace(/<meta property="og:description"[^>]*>\s*/, () => "")
    .replace(/<title>[\s\S]*?<\/title>/, () => tags);
}

/** Text for readers that run no script, and for the page's foot: React only touches #root. */
export function withBody(shell: string, root: string, about: string): string {
  return shell.replace('<div id="root"></div>', () => `<div id="root">${root}</div>${about}`);
}

/** A price in a sentence, written out: search results and readers do not know the app's subscript zeros. */
export function plainPrice(n: number | null): string {
  if (n == null || n >= 0.0001) return price(n);
  const digits = Math.min(20, Math.ceil(-Math.log10(n)) + 3);
  return `$${n.toFixed(digits).replace(/0+$/, "")}`;
}

const PORTAL = "https://app.aurelia.build";
const when = (at: Date) => `${at.toISOString().slice(0, 16).replace("T", " ")} UTC`;
const count = (n: number | null | undefined) => (n == null ? "" : n.toLocaleString("en-US"));
const fact = (label: string, value: string) => (value ? `<div><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>` : "");
/** Before the app has loaded, and for readers that run no script. The app replaces #root. */
const prerender = (inner: string) => `<main class="main prerender">${inner}</main>`;
const tokenLink = (r: Row, slug: string) => `<a href="/${slug}/${esc(r.token.address)}">${esc(clean(r.token.symbol, 24))}</a>`;
const breadcrumb = (items: { name: string; item: string }[]) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((c, i) => ({ "@type": "ListItem", position: i + 1, ...c })),
});

export type Hub = { name: string; slug: string };

/** Every listed chain's token list, and what else the site holds: the links a crawler walks from. */
function siteLinks(hubs: Hub[]): string {
  return `<ul class="about-links">${hubs.map((h) => `<li><a href="/${h.slug}">${esc(h.name)} tokens</a></li>`).join("")}<li><a href="/pulse">New launches</a></li><li><a href="${PORTAL}/docs">API docs</a></li></ul>`;
}

/** `launchpad` is the launchpad's display name, `related` the tokens to link on to, `hubs` the site's chain pages. */
export function tokenPage(shell: string, r: Row, chainName: string, slug: string, at: Date, more: { launchpad?: string; related?: Row[]; hubs?: Hub[] } = {}): string {
  const { launchpad = "", related = [], hubs = [] } = more;
  const symbol = clean(r.token.symbol, 24);
  const name = clean(r.token.name, 60);
  const url = `${SITE}/${slug}/${r.token.address}`;
  const named = `${name && name !== symbol ? `${name} (${symbol})` : symbol} price and chart on ${chainName} | Aurelia`;
  const title = named.length <= 60 ? named : `${symbol} price and chart on ${chainName} | Aurelia Screener`;
  // No liquidity here: the row's is one pool's, and the page above shows the token's, a different figure.
  const facts = [
    `price ${plainPrice(r.priceUsd)}`,
    r.marketCapUsd != null ? `market cap ${compact(r.marketCapUsd, "$")}` : "",
    r.volume24hUsd != null ? `24h volume ${compact(r.volume24hUsd, "$")}` : "",
  ].filter(Boolean).join(", ");
  const sentence = [
    `${name} (${symbol}) is a token on ${chainName}.`,
    r.priceUsd != null ? `It trades at ${plainPrice(r.priceUsd)}` : "",
    r.marketCapUsd != null ? `with a market cap of ${compact(r.marketCapUsd, "$")}.` : r.priceUsd != null ? "." : "",
    r.volume24hUsd != null ? `In the last 24 hours it traded ${compact(r.volume24hUsd, "$")}${r.trades24h != null ? ` in ${r.trades24h.toLocaleString("en-US")} trades` : ""}.` : "",
  ].filter(Boolean).join(" ").replace(/ \./g, ".");
  const flagged = (r.flags ?? []).map((f) => f.replaceAll("_", " ")).join(", ");
  const description = `${name} (${symbol}) on ${chainName}: ${facts}. Live chart, trades, holders and pools.`.slice(0, 160);
  const page = withHead(shell, {
    title,
    description,
    canonical: url,
    robots: indexable(r) ? undefined : "noindex",
    ld: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "WebPage", name: title, description, url, dateModified: at.toISOString(), inLanguage: "en" },
        breadcrumb([
          { name: "Aurelia Screener", item: `${SITE}/` },
          { name: `${chainName} tokens`, item: `${SITE}/${slug}` },
          { name: `${symbol} on ${chainName}`, item: url },
        ]),
      ],
    },
  });
  return withBody(
    page,
    prerender(`<h1>${esc(symbol)} price and chart on ${esc(chainName)}</h1>`),
    `<section class="about" aria-label="About ${esc(symbol)}">
<h2>About ${esc(name)}</h2>
<p>${esc(sentence)}</p>
<dl class="facts">
${[
  fact("Price", plainPrice(r.priceUsd)),
  fact("5m", r.change?.["5m"] != null ? pct(r.change["5m"]) : ""),
  fact("1h", r.change?.["1h"] != null ? pct(r.change["1h"]) : ""),
  fact("6h", r.change?.["6h"] != null ? pct(r.change["6h"]) : ""),
  fact("24h", r.change?.["24h"] != null ? pct(r.change["24h"]) : ""),
  fact("Market cap", r.marketCapUsd != null ? compact(r.marketCapUsd, "$") : ""),
  fact("Fully diluted value", r.fdvUsd != null ? compact(r.fdvUsd, "$") : ""),
  fact("24h volume", r.volume24hUsd != null ? compact(r.volume24hUsd, "$") : ""),
  fact("24h trades", count(r.trades24h)),
  fact("24h buys", count(r.buys24h)),
  fact("24h sells", count(r.sells24h)),
  fact("24h traders", count(r.traders24h)),
  fact("Launched on", launchpad),
  fact("Created", r.createdAt != null ? new Date(r.createdAt).toISOString().slice(0, 10) : ""),
  fact("Chain", chainName),
].join("\n")}
</dl>
<p>Contract address: <code>${esc(r.token.address)}</code>.</p>
${r.flags ? `<p>${flagged ? `Its last security check raised: ${esc(flagged)}.` : "Its last security check raised no flags, which is not a guarantee."} <a href="${PORTAL}/docs/security">What the checks mean</a>.</p>` : ""}
<h2>${esc(name)} data API</h2>
<p>Developers can read this token's price, candles, trades and holders from their own code: <code>GET /tokens/${esc(r.chain)}/${esc(r.token.address)}</code> on the <a href="${PORTAL}/chains/${slug}">Aurelia ${esc(chainName)} token API</a>, with 10,000 free credits to start.</p>
${related.length ? `<h2>More tokens on ${esc(chainName)}</h2><ul class="about-links">${related.map((t) => `<li>${tokenLink(t, slug)}</li>`).join("")}<li><a href="/${slug}">All ${esc(chainName)} tokens</a></li></ul>` : ""}
${hubs.length ? siteLinks(hubs) : ""}
<p class="muted">Figures as of ${when(at)}; the page above stays live. Read from the <a href="${PORTAL}/docs/token">Aurelia token API</a>. Not financial advice.</p>
</section>`,
  );
}

/** A chain's most traded tokens: the page a crawler finds them from, the app's list once it has loaded. */
export function chainPage(shell: string, chainName: string, slug: string, rows: Row[], at: Date, hubs: Hub[]): string {
  const url = `${SITE}/${slug}`;
  const title = `${chainName} tokens: prices and charts | Aurelia Screener`;
  const description = `The most traded ${chainName} tokens: live prices, 24h volume and market cap, with charts, trades and holders for each. Built on the Aurelia API.`;
  const top = rows.slice(0, 100);
  const page = withHead(shell, {
    title,
    description,
    canonical: url,
    ld: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage", name: title, description, url, dateModified: at.toISOString(), inLanguage: "en",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: top.map((r, i) => ({ "@type": "ListItem", position: i + 1, name: clean(r.token.symbol, 24), url: `${url}/${r.token.address}` })),
          },
        },
        breadcrumb([{ name: "Aurelia Screener", item: `${SITE}/` }, { name: `${chainName} tokens`, item: url }]),
      ],
    },
  });
  return withBody(
    page,
    prerender(`<h1>${esc(chainName)} tokens</h1>
<p>The most traded ${esc(chainName)} tokens over the last 24 hours, as of ${when(at)}.</p>
<table><thead><tr><th>Token</th><th>Name</th><th>Price</th><th>24h volume</th><th>Market cap</th></tr></thead><tbody>
${top.map((r) => `<tr><td>${tokenLink(r, slug)}</td><td>${esc(clean(r.token.name, 60))}</td><td>${esc(plainPrice(r.priceUsd))}</td><td>${esc(compact(r.volume24hUsd, "$"))}</td><td>${esc(compact(r.marketCapUsd, "$"))}</td></tr>`).join("\n")}
</tbody></table>`),
    `<section class="about" aria-label="About ${esc(chainName)} tokens">
<h2>${esc(chainName)} tokens on Aurelia Screener</h2>
<p>Live prices, charts, trades, holders and pools for every token traded on ${esc(chainName)} that Aurelia indexes. Each figure comes from the <a href="${PORTAL}/chains/${slug}">Aurelia ${esc(chainName)} token API</a>, which you can call yourself. Not financial advice.</p>
${siteLinks(hubs.filter((h) => h.slug !== slug))}
</section>`,
  );
}

/** The home page: the app, with each chain's busiest tokens under it for readers that run no script. */
export function homePage(shell: string, chains: { hub: Hub; rows: Row[] }[], hubs: Hub[]): string {
  const title = "Aurelia Screener: live token charts and new launches";
  const description = "Live token charts, a screener ranked by the fees traders pay, and the Pulse board of new launches across every chain Aurelia indexes.";
  const page = withHead(shell, {
    title,
    description,
    canonical: `${SITE}/`,
    ld: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "WebSite", "@id": `${SITE}/#website`, name: "Aurelia Screener", url: `${SITE}/`, description, publisher: { "@type": "Organization", name: "Aurelia", url: "https://aurelia.build/", logo: "https://aurelia.build/logo-512.png" } },
      ],
    },
  });
  return withBody(
    page,
    prerender(`<h1>Aurelia Screener</h1>
<p>${esc(description)}</p>
${chains.map(({ hub, rows }) => `<h2><a href="/${hub.slug}">${esc(hub.name)} tokens</a></h2><ul>${rows.slice(0, 10).map((r) => `<li>${tokenLink(r, hub.slug)}: ${esc(clean(r.token.name, 60))}, ${esc(plainPrice(r.priceUsd))}</li>`).join("")}</ul>`).join("\n")}`),
    `<section class="about" aria-label="About Aurelia Screener">
<h2>About Aurelia Screener</h2>
<p>Live token charts, trades, holders and pools, a screener ranked by what traders pay in fees, and the Pulse board of new launches. Every figure comes from the <a href="${PORTAL}/docs">Aurelia API</a>, read with an ordinary API key. Not financial advice.</p>
${siteLinks(hubs)}
</section>`,
  );
}

export function pulsePage(shell: string, hubs: Hub[]): string {
  const description = "New token launches, bonding curves close to graduating and fresh graduations, updating live. Built on the Aurelia API.";
  return withBody(
    withHead(shell, { title: "Pulse: new token launches, live | Aurelia Screener", description, canonical: `${SITE}/pulse` }),
    prerender(`<h1>Pulse: new token launches</h1><p>${esc(description)}</p>`),
    `<section class="about" aria-label="About Pulse">
<h2>About Pulse</h2>
<p>${esc(description)} The same board is one request away with the <a href="${PORTAL}/docs/pulse">Pulse API</a>. Not financial advice.</p>
${siteLinks(hubs)}
</section>`,
  );
}

export function sitemap(urls: string[]): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.map((u) => `  <url><loc>${esc(u)}</loc></url>`).join("\n")}
</urlset>
`;
}

// Crawlers may render a token page, which reads the API; they may not trigger a holders read, which
// goes to the chain and spends its budget, nor open wallet pages, which are one person's and not a topic.
export const ROBOTS = `User-agent: *
Allow: /api/tokens
Allow: /api/trades
Allow: /api/chains
Disallow: /api/
Disallow: /api/tokens/*/*/holders
Disallow: /live
Disallow: /shell
Disallow: /*/wallet/

Sitemap: ${SITE}/sitemap.xml
`;
